import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import {
  Box,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Select,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Check } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

import { fetchStatusList, saveStatusSetting, editStatusSetting, getStatusSettings } from "api/apiService";
import { notifyCenter } from "utils/toast";
import StatusAutomations from "./StatusAutomations";

// Soft UI theme forces display:grid + placeItems:center on MuiInputBase-root
// which shrinks the Select click area. Override back to flex.
const SELECT_SX = {
  height: 40,
  "&.MuiInputBase-root": {
    display: "flex !important",
    placeItems: "initial !important",
    padding: "0 !important",
  },
  "& .MuiSelect-select": {
    display: "flex !important",
    alignItems: "center !important",
    minHeight: "40px !important",
    boxSizing: "border-box",
    padding: "0 32px 0 14px !important",
    flex: 1,
    width: "100% !important",
  },
};

function ToggleCard({ active, icon: Icon, label, onToggle }) {
  return (
    <Box
      onClick={onToggle}
      sx={(t) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        border: `1.5px solid ${active ? t.palette.info.main : t.palette.grey[300]}`,
        borderRadius: 2,
        px: 2,
        py: 1.5,
        cursor: "pointer",
        backgroundColor: active ? t.palette.info.light + "20" : "#fff",
        transition: "all 150ms ease",
        "&:hover": {
          backgroundColor: active ? t.palette.info.light + "33" : t.palette.grey[50],
          borderColor: active ? t.palette.info.main : t.palette.grey[400],
        },
      })}
    >
      <SoftBox display="flex" alignItems="center" gap={1.5}>
        <SoftBox
          sx={(t) => ({
            background: active
              ? `linear-gradient(195deg, ${t.palette.info.main}, ${t.palette.info.dark || t.palette.info.main})`
              : t.palette.grey[400],
            color: "#fff",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
          })}
        >
          <Icon sx={{ fontSize: 18 }} />
        </SoftBox>
        <SoftTypography variant="button" fontWeight="bold" sx={{ textTransform: "none" }}>
          {label}
        </SoftTypography>
      </SoftBox>

      <SoftBox
        sx={(t) => ({
          width: 22,
          height: 22,
          borderRadius: "6px",
          border: `2px solid ${active ? t.palette.info.main : t.palette.grey[400]}`,
          backgroundColor: active ? t.palette.info.main : "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
        })}
      >
        {active && <Check sx={{ fontSize: 14 }} />}
      </SoftBox>
    </Box>
  );
}

ToggleCard.propTypes = {
  active: PropTypes.bool.isRequired,
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  onToggle: PropTypes.func.isRequired,
};

function CreateStatusSettings() {
  const navigate = useNavigate();
  const { statusId } = useParams();
  const isEditMode = !!statusId;

  const [isLoading, setIsLoading] = useState(true);
  const [statusList, setStatusList] = useState([]);
  const [usedStatuses, setUsedStatuses] = useState(new Set());

  const [formData, setFormData] = useState({
    status: "",
    show_date: true,
    show_price: true,
    date_text_status: "",
    price_text_status: "",
  });

  // Fetch existing settings for edit-mode + usedStatuses so create-mode can
  // hide statuses that already have an entry.
  useEffect(() => {
    let cancelled = false;

    async function init() {
      setIsLoading(true);
      try {
        const [statusesRes, settingsRes] = await Promise.all([
          fetchStatusList(),
          getStatusSettings({ limit: -1, page_number: 1 }).catch(() => null),
        ]);

        if (cancelled) return;

        if (statusesRes?.status && Array.isArray(statusesRes.status_list)) {
          setStatusList(statusesRes.status_list);
        }

        if (settingsRes?.status && Array.isArray(settingsRes.data)) {
          setUsedStatuses(new Set(settingsRes.data.map((s) => s.status)));
        }

        if (isEditMode) {
          const res = await editStatusSetting(statusId);
          if (!cancelled && res?.status && res.data) {
            const d = res.data;
            setFormData({
              status: d.status || "",
              show_date: String(d.show_date) === "1",
              show_price: String(d.show_price) === "1",
              date_text_status: d.date_text_status || "",
              price_text_status: d.price_text_status || "",
            });
          } else if (!cancelled) {
            notifyCenter("error", "Kunne ikke hente statusinnstillingen", { duration: 3000 });
          }
        }
      } catch (e) {
        console.error("init error", e);
        if (!cancelled) notifyCenter("error", "Noe gikk galt ved henting", { duration: 3000 });
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    init();
    return () => { cancelled = true; };
  }, [statusId, isEditMode]);

  // In create-mode, hide statuses that already have a setting. In edit-mode,
  // show all statuses (current one is already selected).
  const availableStatuses = useMemo(() => {
    if (isEditMode) return statusList;
    return statusList.filter((s) => !usedStatuses.has(s.value));
  }, [statusList, usedStatuses, isEditMode]);

  function setField(field, value) {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }

  function handleToggle(field) {
    setFormData((prev) => {
      const next = { ...prev, [field]: !prev[field] };
      // When toggle goes ON (show the real value), clear the custom text —
      // placeholder explains the field is disabled.
      if (field === "show_date" && next.show_date) next.date_text_status = "";
      if (field === "show_price" && next.show_price) next.price_text_status = "";
      return next;
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const missing = [];
    if (!formData.status) missing.push("Status");
    if (!formData.show_date && !formData.date_text_status?.trim()) missing.push("Datotekst");
    if (!formData.show_price && !formData.price_text_status?.trim()) missing.push("Pristekst");

    if (missing.length) {
      notifyCenter(
        "warning",
        "Vennligst fyll ut: " + missing.join(", "),
        { duration: 3000 }
      );
      return;
    }

    const payload = {
      status: formData.status,
      show_date: formData.show_date ? 1 : 0,
      show_price: formData.show_price ? 1 : 0,
      date_text_status: formData.date_text_status,
      price_text_status: formData.price_text_status,
      ...(isEditMode ? { status_id: statusId } : {}),
    };

    setIsLoading(true);
    try {
      const res = await saveStatusSetting(payload);
      if (res?.status === true) {
        notifyCenter(
          "success",
          isEditMode ? "Statusinnstilling oppdatert!" : "Statusinnstilling lagret!",
          { duration: 2000 }
        );
        navigate("/status-settings");
      } else {
        notifyCenter("error", res?.message || "Noe gikk galt", { duration: 3000 });
      }
    } catch (err) {
      console.error("save error", err);
      notifyCenter("error", "Noe gikk galt ved lagring", { duration: 3000 });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <DashboardLayout>
      <SoftBox py={3} px={{ xs: 1, md: 2 }}>
        <Card sx={{ position: "relative" }}>
          {/* Header */}
          <SoftBox p={3}>
            <SoftTypography variant="h5" fontWeight="bold">
              {isEditMode ? "Rediger statusinnstilling" : "Ny statusinnstilling"}
            </SoftTypography>
            <SoftTypography variant="button" color="text">
Styr hvordan dato og pris vises for denne ordrestatusen. E-postene statusen sender, står
              lenger ned.
            </SoftTypography>
          </SoftBox>
          <Divider sx={{ my: 0 }} />

          {/* Info callout */}
          <SoftBox px={3} pt={3}>
            <SoftBox
              sx={(t) => ({
                display: "flex",
                gap: 1.5,
                p: 2,
                borderRadius: 1.5,
                backgroundColor: t.palette.info.light + "15",
                borderLeft: `4px solid ${t.palette.info.main}`,
              })}
            >
              <InfoOutlinedIcon sx={{ color: "info.main", mt: 0.25 }} />
              <SoftBox>
                <SoftTypography variant="button" fontWeight="bold" display="block" mb={0.5}>
                  Hva gjør togglene?
                </SoftTypography>
                <SoftTypography variant="caption" color="text" component="div" sx={{ lineHeight: 1.6 }}>
                  <strong>Vis pris AV</strong> → ordrelister viser teksten du fyller inn nedenfor
                  (f.eks. «Kvalitetssikres») i stedet for beløpet.
                  <br />
                  <strong>Vis dato AV</strong> → e-postfakturaer viser teksten i stedet for
                  leveransedato.
                </SoftTypography>
              </SoftBox>
            </SoftBox>
          </SoftBox>

          {/* Form */}
          <SoftBox component="form" onSubmit={handleSubmit} p={3}>
            <Grid container spacing={2.5}>
              {/* Status */}
              <Grid item xs={12}>
                <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                  Status
                </SoftTypography>
                <FormControl fullWidth size="small">
                  <Select
                    value={formData.status || ""}
                    displayEmpty
                    onChange={(e) => setField("status", e.target.value)}
                    sx={SELECT_SX}
                    renderValue={(value) => {
                      if (!value) {
                        return (
                          <SoftTypography variant="body2" color="secondary">
                            Velg statustype
                          </SoftTypography>
                        );
                      }
                      const item = statusList.find((s) => s.value === value);
                      return (
                        <SoftTypography variant="body2" color="text">
                          {item?.label || value}
                        </SoftTypography>
                      );
                    }}
                  >
                    <MenuItem value="" disabled>
                      Velg statustype
                    </MenuItem>
                    {availableStatuses.length === 0 && (
                      <MenuItem value="" disabled>
                        {isEditMode
                          ? "Ingen statuser tilgjengelige"
                          : "Alle statuser har innstilling – rediger eksisterende"}
                      </MenuItem>
                    )}
                    {availableStatuses.map((s) => (
                      <MenuItem key={s.value} value={s.value}>
                        {s.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Toggle cards */}
              <Grid item xs={12} sm={6}>
                <ToggleCard
                  active={formData.show_date}
                  icon={CalendarTodayIcon}
                  label="Vis dato"
                  onToggle={() => handleToggle("show_date")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ToggleCard
                  active={formData.show_price}
                  icon={LocalOfferIcon}
                  label="Vis pris"
                  onToggle={() => handleToggle("show_price")}
                />
              </Grid>

              {/* Date text */}
              <Grid item xs={12}>
                <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                  Tilpasset datotekst
                </SoftTypography>
                <SoftInput
                  placeholder={
                    formData.show_date
                      ? "Ikke i bruk – slå av «Vis dato» for å fylle inn"
                      : "f.eks. Kvalitetssikres"
                  }
                  value={formData.date_text_status}
                  onChange={(e) => setField("date_text_status", e.target.value)}
                  disabled={formData.show_date}
                />
              </Grid>

              {/* Price text */}
              <Grid item xs={12}>
                <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                  Tilpasset pristekst
                </SoftTypography>
                <SoftInput
                  placeholder={
                    formData.show_price
                      ? "Ikke i bruk – slå av «Vis pris» for å fylle inn"
                      : "f.eks. Kvalitetssikres"
                  }
                  value={formData.price_text_status}
                  onChange={(e) => setField("price_text_status", e.target.value)}
                  disabled={formData.show_price}
                />
              </Grid>
            </Grid>
          </SoftBox>

          {/* Footer actions */}
          <Divider sx={{ my: 0 }} />
          <SoftBox display="flex" justifyContent="flex-end" gap={1} p={3}>
            <SoftButton
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => navigate("/status-settings")}
            >
              Avbryt
            </SoftButton>
            <SoftButton
              variant="gradient"
              color="info"
              size="small"
              onClick={handleSubmit}
              type="submit"
            >
              {isEditMode ? "Oppdater" : "Lagre"}
            </SoftButton>
          </SoftBox>

          {isLoading && (
            <SoftBox
              sx={{
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(255, 255, 255, 0.85)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 10,
                borderRadius: "inherit",
              }}
            >
              <CircularProgress color="info" size={48} />
            </SoftBox>
          )}
        </Card>

        {/* E-postene og reglene som hører til statusen. Lagres hver for seg,
            uavhengig av «Oppdater» over. */}
        {isEditMode && formData.status && (
          <StatusAutomations status={formData.status} selectSx={SELECT_SX} />
        )}
      </SoftBox>
    </DashboardLayout>
  );
}

export default CreateStatusSettings;
